import {useState, useEffect, useRef} from "react"; 
import {
    DocumentArrowUpIcon, CheckCircleIcon, DocumentTextIcon, PlusCircleIcon,
    CalendarDaysIcon, UserIcon, PaperClipIcon, ArrowDownTrayIcon, TrashIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../components/Spinner";

interface Template { id: number; nom: string; dateAjout: string; auteur: string; type: string; urlFichier: string; }
interface FormulaireTemplate { nom: string; fichier: File | null; }

export default function GestionTemplates(){
    const [templates, setTemplates] = useState<Template[]>([]);
    const [chargement, setChargement] = useState<boolean>(true);
    const [chargementUpload, setChargementUpload] = useState<boolean>(false);
    const [erreur, setErreur] = useState<string>("");
    const [succes, setSucces] = useState<string>("");
    const [templateSelectionne, setTemplateSelectionne] = useState<Template | null>(null);
    const [formulaire, setFormulaire] = useState<FormulaireTemplate>({nom: "", fichier: null});
    const [surLaZone, setSurLaZone] = useState<boolean>(false); 
    const refInputFichier = useRef<HTMLInputElement>(null); 

    useEffect(() => {
        const recupererTemplates = async (): Promise<void> => {
            try {
                const reponse = await fetch(`${process.env.REACT_APP_API_URL}/api/templates`,
                    { credentials: 'include', headers: { "Content-Type": "application/json" } });
                if (!reponse.ok) throw new Error();
                setTemplates(await reponse.json());
            } catch (e) {
                setErreur("Impossible de charger les templates");
            } finally {
                setChargement(false);
            }
        };
        recupererTemplates();
    }, []);

    const gererFichier = (fichier: File): void => {
        if (!fichier.name.endsWith(".docx")) { setErreur("Seuls les fichiers .docx sont acceptés"); return; }
        setErreur("");
        setFormulaire(p => ({...p, fichier, nom: p.nom || fichier.name.replace(".docx", "")}));
    };

    const gererDragOver = (e: React.DragEvent): void => { e.preventDefault(); setSurLaZone(true); };
    const gererDragLeave = (): void => { setSurLaZone(false); };
    const gererDrop = (e: React.DragEvent): void => {
        e.preventDefault(); setSurLaZone(false);
        const fichier = e.dataTransfer.files[0];
        if (fichier) gererFichier(fichier);
    };

    const validerFormulaire = (): boolean => {
        if (!formulaire.nom) { setErreur("Le nom du template est obligatoire"); return false; }
        if (!formulaire.fichier) { setErreur("Veuillez sélectionner un fichier .docx"); return false; }
        return true;
    };

    const gererUpload = async (): Promise<void> => {
        if (!validerFormulaire()) return;
        setErreur(""); setChargementUpload(true);
        try {
            const donnees = new FormData();
            donnees.append("nom", formulaire.nom);
            donnees.append("fichier", formulaire.fichier!);
            const reponse = await fetch(`${process.env.REACT_APP_API_URL}/api/templates`,
                { method: "POST", credentials: 'include', body: donnees });
            if (!reponse.ok) throw new Error();
            const nouveauTemplate: Template = await reponse.json();
            setTemplates(p => [...p, nouveauTemplate]);
            setFormulaire({nom: "", fichier: null});
            setSucces("Template ajouté avec succès !");
            setTimeout(() => setSucces(""), 3000);
        } catch (e) {
            setErreur("Impossible d'uploader le template");
        } finally {
            setChargementUpload(false);
        }
    };

    const supprimerTemplate = async (id: number): Promise<void> => {
        if (!window.confirm("Supprimer ce template ?")) return;
        try {
            const reponse = await fetch(`${process.env.REACT_APP_API_URL}/api/templates/${id}`,
                { method: "DELETE", credentials: 'include' });
            if (!reponse.ok) throw new Error();
            setTemplates(templates.filter(t => t.id !== id));
            if (templateSelectionne?.id === id) setTemplateSelectionne(null);
            setSucces("Template supprimé !");
            setTimeout(() => setSucces(""), 3000);
        } catch (e) {
            setErreur("Impossible de supprimer le template");
        }
    };

    const telechargerTemplate = (url: string, nom: string): void => {
        const lien = document.createElement("a");
        lien.href = url; lien.download = `${nom}.docx`; lien.click();
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Gestion des templates</h1>
                    <p className="text-gray-400 mt-1">Gérez vos modèles de comptes-rendus Word</p>
                </div>
                <div className="px-4 py-2 rounded-xl text-white text-sm font-medium"
                    style={{background: "linear-gradient(90deg, #6B21A8, #F97316)"}}>{templates.length} template(s)</div>
            </div>

            {erreur && <div className="alert alert-error rounded-xl"><span>{erreur}</span></div>}
            {succes && <div className="alert alert-success rounded-xl"><span>{succes}</span></div>}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-2 h-6 rounded-full" style={{background: "linear-gradient(#6B21A8, #F97316)"}}></div>
                    <h2 className="text-xl font-bold text-gray-800">Ajouter un nouveau template</h2>
                </div>
                <div className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${surLaZone ? "border-purple-400 bg-purple-50" : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/50"}`}
                    onDragOver={gererDragOver} onDragLeave={gererDragLeave} onDrop={gererDrop}
                    onClick={() => refInputFichier.current?.click()}>
                    <input ref={refInputFichier} type="file" accept=".docx" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) gererFichier(f); }}/>
                    {formulaire.fichier ? (
                        <div>
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <CheckCircleIcon className="w-8 h-8 text-purple-600"/>
                                <p className="text-purple-700 font-semibold text-lg">{formulaire.fichier.name}</p>
                            </div>
                            <p className="text-gray-400 text-sm">Cliquez pour changer de fichier</p>
                        </div>
                    ) : (
                        <div>
                            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                                style={{background: "linear-gradient(135deg, #6B21A8, #F97316)"}}>
                                <DocumentArrowUpIcon className="w-8 h-8 text-white"/>
                            </div>
                            <p className="font-semibold text-gray-700 text-lg">Glissez-déposez votre fichier .docx</p>
                            <p className="text-gray-400 text-sm mt-1">ou cliquez pour sélectionner</p>
                        </div>
                    )}
                </div>
                <div className="form-control mt-5">
                    <label className="label pb-1"><span className="label-text font-medium text-gray-700">Nom du template *</span></label>
                    <input type="text" placeholder="Ex: Template standard JuniorISEP"
                        className="input input-bordered w-full rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all"
                        value={formulaire.nom} onChange={(e) => setFormulaire(p => ({...p, nom: e.target.value}))} disabled={chargementUpload}/>
                </div>
                <button className="w-full mt-5 py-3 rounded-xl text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
                    style={{background: "linear-gradient(90deg, #6B21A8, #F97316)"}}
                    onClick={gererUpload} disabled={chargementUpload || !formulaire.fichier || !formulaire.nom}>
                    {chargementUpload ? <Spinner size="sm"/> : <><PlusCircleIcon className="w-5 h-5"/>Ajouter le template</>}
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-2 h-6 rounded-full" style={{background: "linear-gradient(#6B21A8, #F97316)"}}></div>
                    <h2 className="text-xl font-bold text-gray-800">Templates disponibles</h2>
                </div>
                {chargement ? (
                    <div className="flex justify-center py-8"><Spinner size="lg" className="text-purple-600"/></div>
                ) : templates.length === 0 ? (
                    <div className="text-center py-16">
                        <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4"/>
                        <p className="text-gray-400 font-medium">Aucun template disponible</p>
                        <p className="text-gray-300 text-sm mt-1">Ajoutez votre premier template ci-dessus</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {templates.map((template) => (
                            <div key={template.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-purple-50/50 transition-colors border border-gray-100">
                                <div className="flex items-center gap-4 flex-1 cursor-pointer" onClick={() => setTemplateSelectionne(template)}>
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                                        style={{background: "linear-gradient(135deg, #6B21A8, #F97316)"}}>
                                        <DocumentTextIcon className="w-5 h-5 text-white"/>
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-800">{template.nom}</p>
                                        <div className="flex items-center gap-3 mt-0.5">
                                            <span className="flex items-center gap-1 text-xs text-gray-400">
                                                <CalendarDaysIcon className="w-3 h-3"/>{new Date(template.dateAjout).toLocaleDateString("fr-FR")}
                                            </span>
                                            <span className="flex items-center gap-1 text-xs text-gray-400">
                                                <UserIcon className="w-3 h-3"/>{template.auteur}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div style={{display: "flex", gap: "8px", flexDirection: "row"}}>
                                    <button className="btn btn-sm rounded-xl text-purple-600 border-purple-200 hover:bg-purple-50 gap-1"
                                        onClick={() => telechargerTemplate(template.urlFichier, template.nom)}>
                                        <ArrowDownTrayIcon className="w-4 h-4"/>Télécharger
                                    </button>
                                    <button className="btn btn-sm rounded-xl text-red-500 border-red-200 hover:bg-red-50 gap-1"
                                        onClick={() => supprimerTemplate(template.id)}>
                                        <TrashIcon className="w-4 h-4"/>Supprimer
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {templateSelectionne && (
                <div className="fixed inset-0 flex items-center justify-center" style={{zIndex: 9999, backgroundColor: "rgba(0,0,0,0.5)"}}>
                    <div className="bg-white max-w-lg w-full mx-4 rounded-2xl p-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
                                style={{background: "linear-gradient(135deg, #6B21A8, #F97316)"}}>
                                <DocumentTextIcon className="w-6 h-6 text-white"/>
                            </div>
                            <div>
                                <h3 className="font-bold text-xl text-gray-800">{templateSelectionne.nom}</h3>
                                <p className="text-gray-400 text-sm">Fichier Word</p>
                            </div>
                        </div>
                        <div className="space-y-4 mb-6 bg-gray-50 rounded-xl p-4">
                            <div className="flex justify-between items-center">
                                <span className="flex items-center gap-2 text-gray-500 text-sm"><CalendarDaysIcon className="w-4 h-4"/>Date d'ajout</span>
                                <span className="font-medium text-gray-800 text-sm">{new Date(templateSelectionne.dateAjout).toLocaleDateString("fr-FR")}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="flex items-center gap-2 text-gray-500 text-sm"><UserIcon className="w-4 h-4"/>Auteur</span>
                                <span className="font-medium text-gray-800 text-sm">{templateSelectionne.auteur}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="flex items-center gap-2 text-gray-500 text-sm"><PaperClipIcon className="w-4 h-4"/>Type</span>
                                <span className="bg-purple-100 text-purple-700 text-xs font-medium px-3 py-1 rounded-full">{templateSelectionne.type}</span>
                            </div>
                        </div>
                        <div className="flex justify-between border-t border-gray-100 pt-4">
                            <div style={{display: "flex", gap: "8px"}}>
                                <button className="btn rounded-xl text-red-500 border-red-200 hover:bg-red-50 gap-1"
                                    onClick={() => supprimerTemplate(templateSelectionne.id)}>
                                    <TrashIcon className="w-4 h-4"/>Supprimer
                                </button>
                                <button className="btn rounded-xl" onClick={() => setTemplateSelectionne(null)}>Fermer</button>
                            </div>
                            <button className="btn rounded-xl text-white border-0 hover:opacity-90 gap-2"
                                style={{background: "linear-gradient(90deg, #6B21A8, #F97316)"}}
                                onClick={() => telechargerTemplate(templateSelectionne.urlFichier, templateSelectionne.nom)}>
                                <ArrowDownTrayIcon className="w-4 h-4"/>Télécharger
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}